import { yearRange, type DocumentFilter } from '@jupi/ui'

// The Documents page keeps its filters in the URL, so a filtered view can be
// shared, survives a reload and works with the back button.
export interface DocumentView {
  q: string
  category: string
  tag: string
  year: number | null
  page: number
}

export const PAGE_SIZE = 25

export const EMPTY_VIEW: DocumentView = { q: '', category: '', tag: '', year: null, page: 1 }

export function viewFromParams(params: URLSearchParams): DocumentView {
  const year = Number(params.get('year'))
  const page = Number(params.get('page'))
  return {
    q: (params.get('q') ?? '').slice(0, 100),
    category: params.get('category') ?? '',
    tag: params.get('tag') ?? '',
    year: Number.isInteger(year) && year >= 1900 && year <= 2200 ? year : null,
    page: Number.isInteger(page) && page >= 1 ? page : 1,
  }
}

// Only what differs from the empty view goes in the URL.
export function paramsFromView(view: DocumentView): URLSearchParams {
  const params = new URLSearchParams()
  if (view.q) params.set('q', view.q)
  if (view.category) params.set('category', view.category)
  if (view.tag) params.set('tag', view.tag)
  if (view.year !== null) params.set('year', String(view.year))
  if (view.page > 1) params.set('page', String(view.page))
  return params
}

export function filterFromView(view: DocumentView): DocumentFilter {
  return {
    q: view.q.trim() || undefined,
    category: view.category || undefined,
    tag: view.tag || undefined,
    ...(view.year !== null ? yearRange(view.year) : {}),
    page: view.page,
    pageSize: PAGE_SIZE,
  }
}

export const hasFilters = (view: DocumentView) => Boolean(view.q || view.category || view.tag || view.year !== null)

// Changing any filter goes back to the first page.
export const withFilter = (view: DocumentView, change: Partial<Omit<DocumentView, 'page'>>): DocumentView => ({
  ...view,
  ...change,
  page: 1,
})
